import React, { useState } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import {
  DatePicker, MuiPickersUtilsProvider, KeyboardDateTimePicker,
} from '@material-ui/pickers';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Button from 'Components/UI/Button';
import Typography from 'Components/UI/Typography';
import MomentUtils from '@date-io/moment';
import { createBaseStyles } from 'Components/UI/styles';

const styles = (theme) => ({
  root: {},
  dialogContainer: {
    color: theme.palette.facet.primaryBlue,
  },
  header: {
    margin: theme.spacing(3),
  },
  title: {
    fontWeight: 'bold',
  },
  selected: {
    backgroundColor: theme.palette.facet.lightGray3,
  },
  datePicker: {
    display: 'flex',
    flex: 1,
  },
  timePicker: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  confirmationButton: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    justifyContent: 'flex-end',
  },
  dateTimePicker: {
    display: 'flex',
    flexDirection: 'row',
    padding: theme.spacing(1),
  },
  dateField: {
    display: 'none',
  },
  dateTimeField: {
    display: 'block',
    width: '100%',
  },
  rootKeyboardDateTimePicker: {
    ...createBaseStyles(theme),
    width: '100%',
  },
});

const ScheduleSendDatePicker = ({
  classes,
  currentDate,
  handlePickDateTime,
}) => {
  const [scheduledDate, setScheduledDate] = useState(currentDate.isValid() ? currentDate : moment().add(1, 'day').set({
    hour: 9,
    minute: 0,
    second: 0,
  }));
  const [scheduledTime, setScheduledTime] = useState(currentDate.isValid() ? currentDate : moment().add(1, 'day').set({
    hour: 9,
    minute: 0,
    second: 0,
  }));

  const handleDateChange = (newDateTime) => {
    if (newDateTime === null) {
      return;
    }
    newDateTime.set({
      hour: scheduledTime.get('hour'),
      minute: scheduledTime.get('minute'),
      second: scheduledTime.get('second'),
    });
    // Check to see if date is before the current time.
    // We want the time box to display that the selected date time is in the past.
    // The datetime field will only show an error if the date is in the past so a work around for times in the past is to change the date to also in the past.
    if (newDateTime.isBefore(moment())) {
      const newTime = newDateTime.clone().add(-1, 'day');
      setScheduledTime(newTime);
      setScheduledDate(newDateTime);
      return;
    }
    // Only set the time if the date is valid or else the time will be reset to 12:00 AM
    if (newDateTime.isValid()) {
      setScheduledTime(newDateTime);
    }
    setScheduledDate(newDateTime);
  };

  const handleTimeChange = (newDateTime) => {
    if (newDateTime === null) {
      return;
    }
    newDateTime.set({
      year: scheduledDate.get('year'),
      month: scheduledDate.get('month'),
      date: scheduledDate.get('date'),
    });
    // Check to see if date is before the current time.
    // We want the time box to display that the selected date time is in the past.
    // The datetime field will only show an error if the date is in the past so a work around for times in the past is to change the date to also in the past.
    if (newDateTime.isBefore(moment())) {
      newDateTime.add(-1, 'day');
      setScheduledTime(newDateTime);
      return;
    }
    // Only set the the date if date is valid or else the time will be reset to 12:00 AM
    if (newDateTime.isValid()) {
      setScheduledDate(newDateTime);
    }
    setScheduledTime(newDateTime);
  };

  const handleConfirm = () => {
    const scheduledDateCopy = scheduledDate.clone();
    scheduledDateCopy.set({
      hour: scheduledTime.get('hour'),
      minute: scheduledTime.get('minute'),
      second: scheduledTime.get('second'),
    });
    handlePickDateTime(scheduledDateCopy, 'custom');
  };

  const currentDateTime = moment();
  const confirmButtonIsDisabled = (scheduledDate.isValid() && scheduledDate.isBefore(currentDateTime)) || (scheduledTime.isValid() && scheduledTime.isBefore(currentDateTime));

  return (
    <MuiPickersUtilsProvider utils={MomentUtils}>
      <div className={classes.dialogContainer}>
        <div className={classes.header}>
          <Typography variant="h3" className={classes.title}>Pick date & time</Typography>
        </div>
        <div className={classes.dateTimePicker}>
          <div className={classes.datePicker}>
            <DatePicker
              autoOk
              disableToolbar
              disablePast
              orientation="landscape"
              variant="static"
              openTo="date"
              value={scheduledDate}
              onChange={handleDateChange}
            />
          </div>
          <div className={classes.timePicker}>
            <div className={classes.dateTimeField}>
              <KeyboardDateTimePicker
                className={classes.rootKeyboardDateTimePicker}
                classes={{
                  root: classes.rootKeyboardDateTimePicker,
                }}
                disablePast
                value={scheduledDate}
                onChange={handleDateChange}
                format="MM/DD/YYYY"
                KeyboardButtonProps={{ className: classes.dateField }}
                minDateMessage="Date should not be in the past"
                test={'schedule-send-date-picker-input'}
              />
            </div>
            <div className={classes.dateTimeField}>
              <KeyboardDateTimePicker
                className={classes.rootKeyboardDateTimePicker}
                classes={{
                  root: classes.rootKeyboardDateTimePicker,
                }}
                disablePast
                value={scheduledTime}
                onChange={handleTimeChange}
                format="hh:mm A"
                KeyboardButtonProps={{ className: classes.dateField }}
                minDateMessage="Date should not be in the past"
              />
            </div>
            <div className={classes.confirmationButton}>
              <Button
                color="primary"
                disableElevation
                onClick={handleConfirm}
                variant="contained"
                disabled={confirmButtonIsDisabled}
                test={'schedule-send-confirm-button'}
              >
                Confirm
              </Button>
            </div>
          </div>
        </div>
      </div>
    </MuiPickersUtilsProvider>
  );
};

ScheduleSendDatePicker.propTypes = {
  classes: PropTypes.object.isRequired,
  currentDate: PropTypes.object.isRequired,
  handlePickDateTime: PropTypes.func.isRequired,
};

export default withStyles(styles)(ScheduleSendDatePicker);
